<template>
  <div
    :class="[
      'html-block',
      positioningClassNames,
      fullWidth && $style.fullWidth
    ]"
    v-bind="
      $ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'internalName'
      })
    "
  >
    <div
      :class="[
        'html-block__inner',
        $style.iframeHost,
        isIframeLoading && $style.iframeHostLoading
      ]"
    >
      <PardotFormLoadingSkeleton
        v-if="isIframeLoading"
        :class="$style.skeleton"
      />
      <iframe
        v-if="iframeSrc"
        :id="iframeId"
        ref="iframeRef"
        :class="$style.testCameraForm"
        :src="iframeSrc"
        class="p-form-content"
        style="width: 100%"
        width="100%"
        scrolling="no"
        :title="iframeTitle"
        @load="onIframeLoad"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { v } from 'h3-valibot';
import PardotFormLoadingSkeleton from '~/components/global/lib/PardotFormLoadingSkeleton.vue';
import type { ITechnicalComponent } from '~~/lib/ContentfulService';
import { parseURL } from '~/utils/validation/parseURL';
import { useIframeResizer } from '~/composables/useIframeResizer';
import { usePardotIframeSrc } from '~/composables/usePardotIframeSrc';
const { $ContentfulLivePreview } = useNuxtApp();

interface Props {
  item: ITechnicalComponent;
}

const props = defineProps<Props>();
const requestUrl = useRequestURL();
const { $textDictionary } = useNuxtApp();

const iframeRef = ref<HTMLIFrameElement>();
const configurationSchema = v.object({
  url: parseURL
});

const configuration = computed(() => {
  const parsed = v.parse(configurationSchema, props.item.fields.configuration);

  requestUrl.searchParams?.forEach((value, key) => {
    parsed.url.searchParams.append(key, value);
  });

  return parsed;
});

const fullWidth = ref(true);
const cols = ref(12);

const iframeId = computed(() => `pardot-form-iframe-${props.item.sys.id}`);
const iframeTitle = computed(
  () =>
    props.item.fields.internalName ??
    $textDictionary.value?.['pardotForm.iframeTitle'] ??
    'Contact form'
);

const { iframeSrc, isIframeLoading, onIframeLoad } = usePardotIframeSrc(
  () => configuration.value.url,
  { preload: true }
);

function useBlockPositioning(
  className: string,
  cols: number,
  colsCentered: boolean,
  contentCentered: boolean
) {
  const positioningClassNames = computed(() => ({
    [`${className}--max-cols-${cols}`]: true,
    [`${className}--cols-even`]: cols % 2 === 0,
    [`${className}--cols-odd`]: cols % 2 !== 0,
    [`${className}--cols-centered`]: colsCentered,
    [`${className}--content-centered`]: contentCentered
  }));

  return {
    positioningClassNames
  };
}

const { positioningClassNames } = useBlockPositioning(
  'html-block',
  cols.value,
  true,
  false
);

if (configuration.value.url) {
  useIframeResizer(iframeRef, iframeId.value);
}
</script>

<style module>
.fullWidth {
  max-width: none !important;
}

.iframeHost {
  position: relative;
  width: 100%;
  min-height: 700px;
}

.iframeHostLoading .testCameraForm {
  opacity: 0;
}

.skeleton {
  position: absolute;
  inset: 0;
  width: 100%;
  min-height: 700px;
}

.testCameraForm {
  width: 100%;
  min-height: 700px;
}
</style>
